import { Injectable } from '@angular/core';
import { Settings } from '../../core';
import { HttpClient } from '@angular/common/http';
import { SampleActivity, CourierDetails, CourierMappingData, CourierStatusIconMapping } from './courier-model';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import * as moment from 'moment';
import { uniqBy } from 'lodash';

@Injectable()
export class CourierModelService {

    public siteShippingUrl = `${this.settings.apiUrl}/investigator/siteshipping`;
    public sampleTrackingSearchUrl = `${this.settings.apiUrl}/sponsor/sampletracker/grouptracking`;

    constructor(
        private settings : Settings,
        private http: HttpClient) {
    }

    public getCourierModelData(lgxCourierShipmentId: string, isSponsorModel = false): Observable<CourierDetails> {
        let request = { lgxShipmentId: lgxCourierShipmentId };
        if (isSponsorModel) {
            return this.http.post(`${this.sampleTrackingSearchUrl}/courier`, request).pipe(
                map((rawData: any) => {
                    if (!rawData || !rawData.courierDetails) {
                        return null;
                    }
                    return this.formatCourierDetails(rawData.courierDetails);
                })
            );
        }
        return this.http.post(`${this.siteShippingUrl}/courierdetails`, request).pipe(
            map((rawData: any) => {
                if (!rawData || !rawData.courierDetailsData || !rawData.courierDetailsData.length) {
                    return null;
                }
                return this.formatCourierDetails(rawData.courierDetailsData[0]);
            })
        );
    }

    public getShipmentActivityHeader() {
        return [
            {
                key: 'courierDescription',
                displayText: 'Shipment Status',
            },
            {
                key: 'formattedDateTime',
                displayText: 'Date/Time'
            },
        ];
    }

    public getCourierDetailsHeader() {
        return  [
            {
                displayText: 'Courier Name',
                key: 'courierName',
            },
            {
                displayText: 'Destination Country/Territory Name',
                key: 'destinationCountry',
            },
            {
                displayText: 'WayBill Number',
                key: 'waybillNumber',
            },
            {
                displayText: 'Receiver',
                key: 'receiverName',
            },
            {
                displayText: 'Origin Country/Territory Name',
                key: 'originCountry',
            },
            {
                displayText: 'Location of Scan',
                key: 'locationOfScan',
            },
            {
                displayText: 'Shipment Status',
                key: 'shipmentStatus',
            },
            {
                displayText: 'Origin',
                key: 'origin',
            },
            {
                displayText: 'Destination',
                key: 'destination',
            }
        ];
    }

    private getCourierStatusMapping(activityData: SampleActivity, courierMappings: CourierMappingData[]) {
        if (!activityData) { return ['generic_icon', activityData.courierDescription]; }
        if (!courierMappings || !courierMappings.length) { return ['generic_icon', activityData.courierDescription]; }
        let mapping = courierMappings.find(t => t.courierStatus === activityData.type);
        if (!mapping || !mapping.portalStatus) { return ['generic_icon', activityData.courierDescription]; }
        let icon = mapping.portalStatus.toLowerCase().replace(' ', '_').replace('/', '_');
        for (var iconName in CourierStatusIconMapping) {
            if (iconName === icon) {
                if (activityData.courierDescription === mapping.portalStatus) {
                    return [iconName, mapping.portalStatus];
                }
                return [iconName, mapping.portalStatus, activityData.courierDescription];
            }
        }
        return ['generic_icon', mapping.portalStatus, activityData.courierDescription];
    }

    private formatCourierActivity(courierDetails: CourierDetails): SampleActivity[] {
        if (!courierDetails || !courierDetails.courierActivityData || !courierDetails.courierActivityData.length) { return []; }
        let formatedData = courierDetails.courierActivityData.map((data: SampleActivity) => {
            let statusMapping = this.getCourierStatusMapping(data, courierDetails.courierStatusMappingData);
            data.activityStatus = 'courier/' + statusMapping[0];
            data.helptext = statusMapping.length === 3 ? statusMapping[2] : '';
            data.activityDescription = statusMapping[1];
            data.activityOccurredonDate = data.courierTimestamp;
            data.formattedDateTime = this.formatDate(data.courierTimestamp, 'DD-MMM-YYYY hh:mm a');
            return data;
        });
        return formatedData;
    }

    private formatCourierDetails(courierDetails: CourierDetails) {
        if (!courierDetails) { return courierDetails; }
        courierDetails.origin = [courierDetails.originCity, courierDetails.originCountry].filter(Boolean).join(',');
        courierDetails.destination = [courierDetails.receiverCity, courierDetails.destinationCountry].filter(Boolean).join(',');
        courierDetails.locationOfScan = [courierDetails.locationOfScanAddressLine1, courierDetails.locationOfScanAddressLine2].filter(Boolean).join(',');
        courierDetails.formatedCourierData = this.formatCourierActivity(courierDetails);
        if (courierDetails.estimatedDeliveryDate && courierDetails.estimatedDeliveryDate.length) {
            let date = new Date(courierDetails.estimatedDeliveryDate);
            courierDetails.formatedDeliveryDate = date ? date.toLocaleString('default', { weekday: 'long' }) + '<br>' + this.formatDate(courierDetails.estimatedDeliveryDate, 'DD-MMM-YYYY') : '';
        } else {
            courierDetails.formatedDeliveryDate = '-';
        }
        return courierDetails;
    }

    private formatDate(date: any, format: string) {
        if (!date || !moment(date).isValid()) { return ''; }
        return moment(date).format(format);
    }
}